import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Music, Crown, Loader2 } from "lucide-react";
import { BACKEND_URL } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import RelatedLinks from "../components/RelatedLinks";

const ArtistsPage = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const q = search ? `&search=${encodeURIComponent(search)}` : "";
        const res = await fetch(`${BACKEND_URL}/api/artists?limit=200${q}`);
        if (res.ok) setArtists(await res.json());
      } catch {} finally {
        setLoading(false);
      }
    };
    const debounce = setTimeout(fetchArtists, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO
        title="Artists — DJsandMCMedia"
        description="Browse independent artists on DJsandMCMedia. Discover new music, explore profiles, and connect with artists worldwide. Submit your music to get featured on The NU Vibe Network."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "DJsandMCMedia Artists",
          "description": "Independent artists featured on DJsandMCMedia and The NU Vibe Network",
          "url": window.location.href,
        }}
      />
      <Header />

      <section className="pt-28 pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="artists-heading">Independent Artists &amp; DJs</h1>
          <p className="text-white/60 text-sm sm:text-base mb-4 max-w-3xl">
            Meet the independent hip-hop, R&amp;B, Southern Soul, Jazz, Latin, and Rock artists powering DJsandMCMedia&trade; and The NU Vibe Network. Every profile links to live streams, upcoming shows, and direct music downloads from artists building their careers without a major label.
          </p>
          <p className="text-white/50 text-sm mb-2 max-w-3xl">
            Looking for something to listen to right now? Head over to the <Link to="/directory" className="text-purple-400 hover:text-purple-300 underline">Radio Directory</Link> to stream 24/7 stations, or check out <Link to="/discover" className="text-purple-400 hover:text-purple-300 underline">Discover</Link> for the latest featured tracks and DJ blends. If you&rsquo;re an artist ready to get heard, <Link to="/submit" className="text-purple-400 hover:text-purple-300 underline">submit your music</Link> to our radio pool.
          </p>
          <p className="text-white/40 text-xs mb-8">
            <strong className="text-white/60">Browse by genre:</strong> Hip-Hop &middot; R&amp;B &middot; Southern Soul &middot; Jazz &middot; Latin &middot; Rock &middot; Gospel &middot; Afrobeats &middot; Reggae &middot; Electronic
          </p>

          {/* Search */}
          <div className="relative mb-10 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500/50 transition"
              placeholder="Search artists..."
              data-testid="artist-search"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
            </div>
          ) : artists.length === 0 ? (
            <div className="text-center py-20">
              <Music className="w-12 h-12 text-white/10 mx-auto mb-4" />
              <p className="text-white/40">{search ? "No artists match your search." : "No artists yet."}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6" data-testid="artists-grid">
              {artists.map(a => (
                <Link
                  key={a.slug}
                  to={`/artist/${a.slug}`}
                  className="group text-center"
                  data-testid={`artist-card-${a.slug}`}
                >
                  <div className="w-full aspect-square rounded-full overflow-hidden bg-white/5 mb-3 border-2 border-transparent group-hover:border-purple-500/50 transition-all group-hover:scale-105">
                    {a.photo_url ? (
                      <img src={a.photo_url} alt={a.artist_name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-500/10 to-pink-500/10">
                        <Music className="w-10 h-10 text-white/15" />
                      </div>
                    )}
                  </div>
                  <p className="text-white/80 text-sm font-medium truncate group-hover:text-white transition flex items-center gap-1 justify-center">
                    {a.artist_name}
                    {a.gender === "male" && (
                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0 inline">
                        <path d="M9 12l2 2 4-4" stroke="#3b82f6" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="10" fill="#3b82f6" fillOpacity="0.15" stroke="#3b82f6" strokeWidth="1.5"/>
                      </svg>
                    )}
                    {a.gender === "female" && (
                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0 inline">
                        <path d="M9 12l2 2 4-4" stroke="#ec4899" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="10" fill="#ec4899" fillOpacity="0.15" stroke="#ec4899" strokeWidth="1.5"/>
                      </svg>
                    )}
                  </p>
                  {a.genre_tags?.length > 0 && (
                    <p className="text-white/30 text-xs truncate mt-0.5">{a.genre_tags.slice(0, 2).join(", ")}</p>
                  )}
                  {a.is_member && (
                    <span className="inline-flex items-center gap-0.5 text-amber-400 text-[10px] mt-1 font-medium">
                      <Crown className="w-3 h-3" /> Member
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <RelatedLinks keys={["directory", "discover", "network", "submit", "news", "advertise"]} heading="More from DJsandMCMedia" theme="purple" />

      <Footer />
    </div>
  );
};

export default ArtistsPage;
